import {useMutation, useQuery} from '@tanstack/react-query';
import {AxiosError} from 'axios';

import {apiClient} from '../foundation/apiClient';
import {
    OnboardingRequestDTO,
    APIResponse,
    EditRequestDTO,
    APIResponseType,
    HttpResponse,
    EditWebsiteRequestDTO,
    UserSession,
    SuccessStateWithAction,
    CampaignInfo,
    UserCreationRequestDTO,
} from './models';
import {useEventBatching} from './events';

export const makeOnboardingRequest = async (onboardingRequestDTO: OnboardingRequestDTO): Promise<APIResponse> => {
    const data = await apiClient.post('onboard', onboardingRequestDTO);
    return data.data;
};

export const makeEditRequest = async (websiteId: string, updateData: EditRequestDTO): Promise<APIResponse> => {
    const data = await apiClient.post('edit', updateData, {
        params: {
            id: websiteId,
        },
    });
    return data.data;
};

export const editWebsiteRequestDTO = async (editWebsiteRequestDTO: EditWebsiteRequestDTO): Promise<APIResponse> => {
    const path = 'structure/edit';
    const data = await apiClient.post(path, editWebsiteRequestDTO);
    return data.data;
};

const getWebsiteDetails = async (subDomain: string): Promise<APIResponse> => {
    const path = 'website/fetch';
    const data = await apiClient.get(path, {
        params: {
            subDomain: subDomain,
        },
    });
    return data.data;
};

const getDashboardDetails = async (): Promise<APIResponse> => {
    const path = 'dashboard/fetch';
    const data = await apiClient.get(path);
    return data.data;
};

export const useDashboardDetails = () => {
    return useQuery({
        queryKey: ['retailer-homepages'],
        queryFn: () => getDashboardDetails(),
    });
};

export const useWebsiteDetails = (subDomain: string) => {
    return useQuery({
        queryKey: ['retailer-homepages', subDomain],
        queryFn: () => getWebsiteDetails(subDomain),
        staleTime: Infinity,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    });
};

export const useOnboardNewUser = () => {
    const {trackEvent} = useEventBatching();

    const {mutate, ...rest} = useMutation({
        mutationFn: (data: OnboardingRequestDTO) => makeOnboardingRequest(data),
        retry: false,
    });

    const onboardNewUser = ({
        onboardingRequestDTO,
        onError,
        onSuccess,
    }: {
        onboardingRequestDTO: OnboardingRequestDTO;
        onError: (message: string) => void;
        onSuccess: (responseData: SuccessStateWithAction) => void;
    }) => {
        mutate(onboardingRequestDTO, {
            onSuccess: (response) => {
                if (response.type === APIResponseType.SuccessStateWithAction) {
                    const responseData = response.data as SuccessStateWithAction;
                    onSuccess(responseData);
                }
            },
            onError: (error) => {
                if (error.name === 'AxiosError') {
                    const err = error as AxiosError;
                    const message = err.response?.data as APIResponse;
                    if (message.type === APIResponseType.ErrorString) {
                        const errorMessageDetails = message.data as HttpResponse;
                        onError(errorMessageDetails.message);
                        trackEvent({
                            eventName: 'USER_ONBOARDING_ERROR',
                            props: {
                                error: error.toString(),
                                errorMessage: errorMessageDetails.message,
                            },
                        });
                    }
                } else {
                    onError?.(error.message);
                }
            },
        });
    };

    return {onboardNewUser, ...rest};
};

interface EditWebsiteMutationParams {
    editRequestDTO: EditRequestDTO;
    cb?: VoidFunction;
}

export const useEditForm = (params: {websiteId: string}) => {
    const {trackEvent} = useEventBatching();
    const {mutate, ...rest} = useMutation({
        mutationFn: (data: EditRequestDTO) => makeEditRequest(params.websiteId, data),
        retry: false,
    });

    const editWebsiteInfo = ({
        data,
        throwSnackbarMessage,
    }: {
        data: EditWebsiteMutationParams;
        throwSnackbarMessage: (message: string) => void;
    }) => {
        mutate(data.editRequestDTO, {
            onSuccess: (response) => {
                if (response.type === APIResponseType.SuccessfulString) {
                    throwSnackbarMessage((response.data as HttpResponse).message);
                }
                data.cb?.();
            },
            onError: (error) => {
                if (error.name === 'AxiosError') {
                    const err = error as AxiosError;
                    const message = err.response?.data as APIResponse;
                    if (message.type === APIResponseType.ErrorString) {
                        throwSnackbarMessage((message.data as HttpResponse).message);
                        trackEvent({
                            eventName: 'EDIT_WEBSITE_ERROR',
                            props: {
                                error: error.toString(),
                                errorMessage: (message.data as HttpResponse).message,
                            },
                        });
                    }
                } else {
                    throwSnackbarMessage?.(error.message);
                }
            },
        });
    };

    return {editWebsiteInfo, ...rest};
};

export const getUserSession = async (): Promise<UserSession> => {
    const path = 'user/session';
    const data = await apiClient.get(path);
    return data.data;
};

interface MutationParams {
    editWebsiteRequestDTO: EditWebsiteRequestDTO;
    cb?: VoidFunction;
}

export const useUpdateWebsiteStructure = () => {
    const {mutate, ...rest} = useMutation({
        mutationFn: (data: EditWebsiteRequestDTO) => editWebsiteRequestDTO(data),
        retry: false,
    });
    const {trackEvent} = useEventBatching();

    const mutateWebsiteStructure = ({
        data,
        throwSnackbarMessage,
    }: {
        data: MutationParams;
        throwSnackbarMessage: (message: string) => void;
    }) => {
        mutate(data.editWebsiteRequestDTO, {
            onSuccess: async (response) => {
                if (response.type === APIResponseType.SuccessfulString) {
                    throwSnackbarMessage((response.data as HttpResponse).message);
                    data.cb?.();
                } else if (response.type === APIResponseType.ErrorString) {
                    throwSnackbarMessage((response.data as HttpResponse).message);
                }
            },
            onError: (error) => {
                if (error.name === 'AxiosError') {
                    const err = error as AxiosError;
                    const message = err.response?.data as APIResponse;
                    if (message.type === APIResponseType.ErrorString) {
                        throwSnackbarMessage((message.data as HttpResponse).message);
                        trackEvent({
                            eventName: 'EDIT_WEBSITE_STRUCTURE_ERROR',
                            props: {
                                error: error.toString(),
                                errorMessage: (message.data as HttpResponse).message,
                            },
                        });
                    }
                } else {
                    throwSnackbarMessage?.(error.message);
                }
            },
        });
    };

    return {mutateWebsiteStructure, ...rest};
};

export const updateCampaignSource = async (campaignInfo: CampaignInfo): Promise<void> => {
    const response = await apiClient.post('user/campaign/update', campaignInfo);
    return response.data;
};

export const onboardNewUser = async (userCreationRequestDTO: UserCreationRequestDTO): Promise<void> => {
    await apiClient.post('user/onboard', userCreationRequestDTO);
};
